import React, { useState, useEffect, useReducer, useContext, useRef, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import SortIcon from "@material-ui/icons/Sort";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";
import TicketContextMenu from "../TicketContextMenu";

import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  canAccessTicket,
  canSeeTicketInList,
  isWithinConnectionScope,
  isAdminUser,
  getManagedWhatsappIds,
} from "../../helpers/canAccessTicket";

const ALARM_TICK_INTERVAL_MS = 30000;

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    // "auto" e nao "scroll": com "scroll" a calha de 8px fica reservada mesmo
    // com poucos tickets, sobrando uma faixa vazia na lateral da lista.
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
  },

  sortButton: {
    padding: 8,
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  selectionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },

  selectionInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  selectionActions: {
    display: "flex",
    gap: 8,
  },
}));

// Os eventos de socket carregam a conexão em graus diferentes de detalhe —
// alguns emissores mandam só `{id, name, type}`, outros omitem a conexão. Como
// o reducer substitui o ticket inteiro, sem este merge o ícone da conexão perde
// a cor (fica cinza) ou some da lista até a página ser recarregada.
const mergeWhatsapp = (currentTicket, incomingTicket) => {
  if (!incomingTicket?.whatsapp) return currentTicket?.whatsapp;
  if (!currentTicket?.whatsapp) return incomingTicket.whatsapp;
  const currentId = currentTicket.whatsapp.id;
  const incomingId = incomingTicket.whatsapp.id;
  // Sem os dois ids não dá para afirmar que a conexão mudou — o emissor de nova
  // mensagem, por exemplo, manda só `{name}`. Tratar como troca de conexão aí
  // descartava id e cor, e o badge sumia do card até recarregar a página.
  if (
    currentId != null &&
    incomingId != null &&
    Number(currentId) !== Number(incomingId)
  ) {
    // Ticket mudou de conexão: a nova manda, sem herdar campos da anterior
    return incomingTicket.whatsapp;
  }
  return { ...currentTicket.whatsapp, ...incomingTicket.whatsapp };
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    // Defensivo: se a resposta vier inesperada (ex.: erro no /tickets), não quebra a tela
    const newTickets = Array.isArray(action.payload) ? action.payload : [];

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Criar novo array com ticket atualizado para forçar re-render
      const newState = [...state];
      const currentTicket = state[ticketIndex];
      newState[ticketIndex] = {
        ...ticket,
        whatsapp: mergeWhatsapp(currentTicket, ticket),
        isInAlarm:
          ticket.isInAlarm !== undefined
            ? ticket.isInAlarm
            : Boolean(currentTicket?.isInAlarm),
      };
      return newState;
    } else {
      return [...state, ticket];
    }
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Criar novo array com ticket atualizado para forçar re-render
      const newState = [...state];
      const currentTicket = state[ticketIndex];
      newState[ticketIndex] = {
        ...ticket,
        whatsapp: mergeWhatsapp(currentTicket, ticket),
        isInAlarm:
          ticket.isInAlarm !== undefined
            ? ticket.isInAlarm
            : Boolean(currentTicket?.isInAlarm),
      };
      return newState;
    } else {
      return [...state, ticket];
    }
  }

  // O mesmo contato pode ter um chamado por conexão (unifyTicketsByContact=disabled).
  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    return state.map((t) => (t.contactId === contact.id ? { ...t, contact } : t));
  }

  if (action.type === "DELETE_TICKETS_BY_CONTACT") {
    const contactId = Number(action.payload);
    return state.filter((t) => Number(t.contactId) !== contactId);
  }

  // Conexão excluída: os tickets dela foram fechados e inativados no backend.
  if (action.type === "DELETE_TICKETS_BY_WHATSAPP") {
    const whatsappId = Number(action.payload);
    return state.filter((t) => Number(t.whatsappId) !== whatsappId);
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "MOVE_TICKET_TO_TOP") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Remove o ticket da posição atual
      const [existingTicket] = state.splice(ticketIndex, 1);
      // Atualiza com os novos dados
      const updatedTicket = { ...existingTicket, ...ticket };
      // Adiciona no início (topo)
      state.unshift(updatedTicket);
    } else {
      // Se não existe, adiciona no topo
      state.unshift(ticket);
    }
    return [...state];
  }

  if (action.type === "MOVE_TICKET_TO_BOTTOM") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Remove o ticket da posição atual
      const [existingTicket] = state.splice(ticketIndex, 1);
      // Atualiza com os novos dados
      const updatedTicket = { ...existingTicket, ...ticket };
      // Adiciona no final da lista
      state.push(updatedTicket);
    } else {
      // Se não existe, adiciona no final
      state.push(ticket);
    }
    return [...state];
  }

  // Atualiza ticket in-place se existe, adiciona no topo se é novo
  // Usado quando autoReorder está desabilitado
  if (action.type === "UPSERT_TICKET_TOP") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Ticket já existe: atualiza in-place sem mudar posição
      const newState = [...state];
      const currentTicket = state[ticketIndex];
      newState[ticketIndex] = {
        ...currentTicket,
        ...ticket,
        whatsapp: mergeWhatsapp(currentTicket, ticket),
        isInAlarm:
          ticket.isInAlarm !== undefined
            ? ticket.isInAlarm
            : Boolean(currentTicket?.isInAlarm),
      };
      return newState;
    } else {
      // Ticket novo: adiciona no topo
      return [ticket, ...state];
    }
  }

  if (action.type === "SORT_TICKETS") {
    const { sortOrder } = action.payload;
    const sortedState = [...state].sort((a, b) => {
      if (sortOrder === "unreadFirst") {
        // Tickets com markedAsUnread=1 ou unreadMessages>0 aparecem primeiro
        const isUnreadA = Number(a.markedAsUnread || 0) === 1 || Number(a.unreadMessages || 0) > 0;
        const isUnreadB = Number(b.markedAsUnread || 0) === 1 || Number(b.unreadMessages || 0) > 0;

        if (isUnreadA && !isUnreadB) return -1;
        if (!isUnreadA && isUnreadB) return 1;

        // Ambos com ou sem não lidas: ordenar por updatedAt desc
        const dateA = new Date(a.updatedAt).getTime();
        const dateB = new Date(b.updatedAt).getTime();
        return dateB - dateA;
      }

      if (sortOrder === "waitTimeDesc" || sortOrder === "waitTimeAsc") {
        // Tempo de espera baseado na última mensagem do cliente (fromMe=0)
        // Se lastHumanMessageAt >= lastClientMessageAt, ticket já foi respondido → tempo = 0
        const clientDateA = new Date(a.lastClientMessageAt || a.createdAt).getTime();
        const clientDateB = new Date(b.lastClientMessageAt || b.createdAt).getTime();
        const replyDateA = a.lastHumanMessageAt ? new Date(a.lastHumanMessageAt).getTime() : 0;
        const replyDateB = b.lastHumanMessageAt ? new Date(b.lastHumanMessageAt).getTime() : 0;

        // Ticket respondido se a última resposta é >= última msg do cliente
        const repliedA = replyDateA > 0 && replyDateA >= clientDateA;
        const repliedB = replyDateB > 0 && replyDateB >= clientDateB;

        // Tempo de espera efetivo (0 se respondido, senão timestamp da msg do cliente)
        const waitA = repliedA ? 0 : clientDateA;
        const waitB = repliedB ? 0 : clientDateB;

        if (sortOrder === "waitTimeDesc") {
          // Maior tempo de espera primeiro: não respondidos com msg mais antiga primeiro
          // Não respondidos antes de respondidos, depois por data mais antiga
          if (repliedA !== repliedB) return repliedA ? 1 : -1;
          if (repliedA && repliedB) return 0; // Ambos respondidos, tempo = 0
          return waitA - waitB; // Ambos não respondidos: msg mais antiga = maior espera
        } else {
          // Menor tempo de espera primeiro: respondidos primeiro, depois por data mais recente
          if (repliedA !== repliedB) return repliedA ? -1 : 1;
          if (repliedA && repliedB) return 0; // Ambos respondidos, tempo = 0
          return waitB - waitA; // Ambos não respondidos: msg mais recente = menor espera
        }
      } else {
        const dateA = new Date(a.updatedAt).getTime();
        const dateB = new Date(b.updatedAt).getTime();
        if (sortOrder === "desc") {
          return dateB - dateA; // Mais recentes primeiro
        } else {
          return dateA - dateB; // Mais antigos primeiro
        }
      }
    });
    return sortedState;
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    searchBy = "all",
    tags,
    contactTags,
    users,
    showAll,
    selectedQueueIds,
    selectedWhatsappIds,
    updateCount,
    updateAlarmSummary,
    style,
    sortOrder = "desc", // Recebe sortOrder como prop
    sortKey = 0, // Recebe sortKey como prop para forçar refresh
    autoReorder = false, // Recebe autoReorder como prop
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);

  // Config `ticketConnectionScope`: quando ligada, o gate de tempo-real abaixo
  // impede que tickets de conexões fora do escopo do usuário apareçam ao vivo
  // (o backend já restringe a listagem inicial).
  const { connectionScopeEnabled } = useContext(SettingsContext);

  // Estado para seleção múltipla
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [selectionStatus, setSelectionStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());

  // Estado para menu de contexto (agora com posição do mouse)
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const [contextMenuTicket, setContextMenuTicket] = useState(null);
  const pendingContextMenuRef = useRef(null);

  // Ref para o container de scroll para preservar posição durante reordenação
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const ticketsListRef = useRef(ticketsList);

  // Filtro por tags do contato em tempo real: os eventos de ticket/mensagem não
  // trazem `contact.tags` e o reducer substitui o ticket inteiro, então as tags
  // conhecidas de cada contato ficam guardadas aqui (contactId -> tagIds).
  const contactTagsByContactRef = useRef(new Map());
  const lastContactTagRecheckRef = useRef(0);
  const contactTagRecheckTimerRef = useRef(null);
  const pendingContactTagFetchesRef = useRef(new Set());

  useEffect(() => () => clearTimeout(contactTagRecheckTimerRef.current), []);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, searchBy, dispatch, showAll, tags, contactTags, users, selectedQueueIds, selectedWhatsappIds, sortOrder, sortKey]);

  useEffect(() => {
    ticketsListRef.current = ticketsList;
  }, [ticketsList]);

  // Quando autoReorder é ativado, ordena a lista imediatamente
  useEffect(() => {
    if (autoReorder && ticketsList.length > 0) {
      dispatch({ type: "SORT_TICKETS", payload: { sortOrder } });
    }
  }, [autoReorder, sortOrder, ticketsList.length]);

  const { tickets, hasMore, loading, alarmSummary } = useTickets({
    pageNumber,
    searchParam,
    searchBy,
    status,
    showAll,
    tags: JSON.stringify(tags),
    contactTags:
      Array.isArray(contactTags) && contactTags.length > 0
        ? JSON.stringify(contactTags)
        : undefined,
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
    whatsappIds: JSON.stringify(selectedWhatsappIds),
    sortOrder,
    sortKey,
    refreshTrigger,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(Date.now());
    }, ALARM_TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Permissões que definem o escopo da lista (allTicket, conexões gerenciadas,
  // filas, escopo por conexão). Quando o admin altera o usuário, o objeto
  // `user` chega via socket (useAuth) — sem este refetch imediato, REVOGAR
  // parecia instantâneo (os eventos de socket removem os tickets um a um), mas
  // CONCEDER exigia F5: os tickets recém-liberados nunca eram buscados.
  const permissionSignature = JSON.stringify({
    allTicket: user?.allTicket,
    managed: getManagedWhatsappIds(user),
    queues: (user?.queues || [])
      .map((q) => Number(q.id))
      .sort((a, b) => a - b),
    scope: connectionScopeEnabled,
  });
  const permissionSignatureRef = useRef(permissionSignature);
  useEffect(() => {
    if (permissionSignatureRef.current === permissionSignature) return;
    permissionSignatureRef.current = permissionSignature;
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setRefreshTrigger(Date.now());
  }, [permissionSignature]);

  // Atualiza ticketsList sempre que tickets mudar
  useEffect(() => {
    // O backend já retorna apenas os tickets das filas selecionadas
    // Não precisamos filtrar novamente aqui
    dispatch({ type: "LOAD_TICKETS", payload: tickets });

    if (Array.isArray(tickets)) {
      tickets.forEach((ticket) => {
        const contactId = Number(ticket?.contactId ?? ticket?.contact?.id);
        if (Number.isNaN(contactId) || !Array.isArray(ticket?.contact?.tags)) return;
        contactTagsByContactRef.current.set(
          contactId,
          ticket.contact.tags.map((t) => Number(t?.id ?? t)).filter((id) => !Number.isNaN(id))
        );
      });
    }
  }, [tickets]);


  // Função para salvar a posição de scroll atual
  const saveScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
  }, []);

  // Função para restaurar a posição de scroll
  const restoreScrollPosition = useCallback(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      // Usar requestAnimationFrame para garantir que o DOM foi atualizado
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const normalizedSelectedQueueIds = Array.isArray(selectedQueueIds)
      ? selectedQueueIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    const normalizedSelectedWhatsappIds = Array.isArray(selectedWhatsappIds)
      ? selectedWhatsappIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    const normalizedTagIds = Array.isArray(tags)
      ? tags.map((t) => Number(t?.id ?? t)).filter((id) => !Number.isNaN(id))
      : [];
    const normalizedUserIds = Array.isArray(users)
      ? users.map((u) => Number(u?.id ?? u)).filter((id) => !Number.isNaN(id))
      : [];
    const normalizedContactTagIds = Array.isArray(contactTags)
      ? contactTags.map((t) => Number(t?.id ?? t)).filter((id) => !Number.isNaN(id))
      : [];
    const hasContactTagFilter = normalizedContactTagIds.length > 0;

    const toTagIds = (list) =>
      list.map((t) => Number(t?.id ?? t?.tagId ?? t)).filter((id) => !Number.isNaN(id));

    // Tags do contato do ticket: as do payload (quando vierem) ou as já
    // conhecidas. `null` = desconhecidas (contato que ainda não está na lista).
    const knownContactTagIds = (ticket) => {
      const contactId = Number(ticket?.contactId ?? ticket?.contact?.id);
      if (Array.isArray(ticket?.contact?.tags)) {
        const ids = toTagIds(ticket.contact.tags);
        if (!Number.isNaN(contactId)) {
          contactTagsByContactRef.current.set(contactId, ids);
        }
        return ids;
      }
      if (Number.isNaN(contactId)) return null;
      return contactTagsByContactRef.current.get(contactId) ?? null;
    };

    // OU: basta o contato ter uma das tags selecionadas.
    // true / false / null (desconhecido — só o servidor sabe).
    const matchesContactTags = (ticket) => {
      if (!hasContactTagFilter) return true;
      const ids = knownContactTagIds(ticket);
      if (ids === null) return null;
      return normalizedContactTagIds.some((id) => ids.includes(id));
    };

    // Um ticket de contato com tags desconhecidas não entra direto na lista: a
    // listagem é refeita no servidor, no máximo uma vez a cada 5 s, para não
    // virar uma requisição por mensagem recebida. Pedidos dentro da janela não
    // se perdem: viram uma única consulta ao fim dela.
    const requestContactTagRecheck = () => {
      if (contactTagRecheckTimerRef.current) return;
      const wait = Math.max(0, 5000 - (Date.now() - lastContactTagRecheckRef.current));
      contactTagRecheckTimerRef.current = setTimeout(() => {
        contactTagRecheckTimerRef.current = null;
        lastContactTagRecheckRef.current = Date.now();
        setRefreshTrigger(Date.now());
      }, wait);
    };

    // Contato com tags desconhecidas: busca só as tags dele (uma vez por
    // contato) e, se casar com o filtro, refaz a listagem para trazer o ticket.
    // Sem isso, cada mensagem de um contato fora do filtro pediria a lista inteira.
    const contactTagsAbortController = new AbortController();
    const resolveUnknownContactTags = (ticket) => {
      const contactId = Number(ticket?.contactId ?? ticket?.contact?.id);
      if (Number.isNaN(contactId)) return;
      if (pendingContactTagFetchesRef.current.has(contactId)) return;
      pendingContactTagFetchesRef.current.add(contactId);

      api
        .get(`/contact-tags/${contactId}`, {
          signal: contactTagsAbortController.signal,
        })
        .then(({ data }) => {
          const ids = Array.isArray(data?.tagIds) ? toTagIds(data.tagIds) : [];
          contactTagsByContactRef.current.set(contactId, ids);
          if (normalizedContactTagIds.some((id) => ids.includes(id))) {
            requestContactTagRecheck();
          }
        })
        .catch(() => {
          // Silencioso: o ticket aparece no próximo refresh periódico se casar.
        })
        .finally(() => {
          pendingContactTagFetchesRef.current.delete(contactId);
        });
    };

    const matchesSearchParam = (ticket) => {
      if (!searchParam || !searchParam.trim()) return true;

      const normalizedSearch = searchParam.toLowerCase().trim();
      const contactName = (ticket?.contact?.name || "").toLowerCase();

      if (searchBy === "contact") {
        return contactName.includes(normalizedSearch);
      }

      const contactNumber = (ticket?.contact?.number || "").toLowerCase();
      const lastMessage = (ticket?.lastMessage || "").toLowerCase();
      const ticketId = ticket?.id ? String(ticket.id) : "";
      const ticketUuid = (ticket?.uuid || "").toLowerCase();

      return (
        contactName.includes(normalizedSearch) ||
        contactNumber.includes(normalizedSearch) ||
        lastMessage.includes(normalizedSearch) ||
        ticketId.includes(normalizedSearch) ||
        ticketUuid.includes(normalizedSearch)
      );
    };

    const matchesTags = (ticket) => {
      if (normalizedTagIds.length === 0) return true;
      const ticketTags = Array.isArray(ticket?.tags)
        ? ticket.tags
        : ticketsListRef.current.find((t) => t.id === ticket?.id)?.tags;

      if (!Array.isArray(ticketTags)) return false;

      const ticketTagIds = ticketTags
        .map((t) => Number(t?.id ?? t?.tagId ?? t))
        .filter((id) => !Number.isNaN(id));

      return normalizedTagIds.every((id) => ticketTagIds.includes(id));
    };

    const matchesUsers = (ticket) => {
      if (normalizedUserIds.length === 0) return true;
      const ticketUserId = ticket?.userId ? Number(ticket.userId) : null;
      return ticketUserId !== null && normalizedUserIds.includes(ticketUserId);
    };

    const matchesWhatsapp = (ticket) => {
      if (normalizedSelectedWhatsappIds.length === 0) return true;
      const ticketWhatsappId = ticket?.whatsappId ? Number(ticket.whatsappId) : null;
      return ticketWhatsappId !== null && normalizedSelectedWhatsappIds.includes(ticketWhatsappId);
    };

    // Escopo por conexão (config `ticketConnectionScope`). Espelha a regra do backend
    // em tempo real: usuário não-admin só recebe tickets das conexões que controla
    // (conexão padrão ∪ conexões atribuídas). Sem conexões => não recebe nada.
    const matchesConnectionScope = (ticket) =>
      isWithinConnectionScope(user, ticket, connectionScopeEnabled);

    const matchesBaseFilters = (ticket) => {
      // Comparar IDs de forma segura (converter para número para evitar problemas de tipo)
      const ticketUserId = ticket.userId ? Number(ticket.userId) : null;
      const currentUserId = user?.id ? Number(user.id) : null;

      const userMatch = !ticketUserId || ticketUserId === currentUserId || showAll;

      // Verificação de fila
      let queueMatch = false;
      if (!ticket.queueId) {
        // Ticket sem fila: quem manda é a regra de acesso do backend
        // (helpers/CanAccessTicket.ts). Mostrar sem essa checagem gerava ticket
        // "fantasma": aparecia na lista em tempo real, mas ao clicar dava
        // "Acesso não permitido" e sumia no F5.
        queueMatch = canAccessTicket(user, ticket, connectionScopeEnabled);
      } else {
        // Ticket com fila: verificar se está nas filas selecionadas
        const ticketQueueId = Number(ticket.queueId);
        queueMatch = normalizedSelectedQueueIds.includes(ticketQueueId);
      }

      const statusMatch = status ? ticket.status === status : true;

      return (
        canSeeTicketInList(user, ticket) &&
        userMatch &&
        queueMatch &&
        statusMatch &&
        matchesTags(ticket) &&
        matchesUsers(ticket) &&
        matchesWhatsapp(ticket) &&
        matchesConnectionScope(ticket)
      );
    };

    const matchesEverythingExceptSearch = (ticket) =>
      matchesBaseFilters(ticket) && matchesContactTags(ticket) === true;

    const shouldUpdateTicket = (ticket) =>
      matchesEverythingExceptSearch(ticket) && matchesSearchParam(ticket);

    // Passaria em tudo, mas as tags do contato ainda não são conhecidas.
    const needsContactTagRecheck = (ticket) =>
      hasContactTagFilter &&
      matchesContactTags(ticket) === null &&
      matchesBaseFilters(ticket) &&
      matchesSearchParam(ticket);

    // O filtro local de busca só enxerga nome, número e a última mensagem — o
    // servidor também procura dentro do corpo das mensagens. Quando o ticket passa
    // em todo o resto e só o texto não bate (ex.: aceitar um chamado encontrado
    // pela busca faz ele migrar de AGUARDANDO para ATENDENDO), refaz a consulta em
    // vez de descartá-lo; assim ele não some da lista sem motivo aparente.
    const needsServerRecheck = (ticket) =>
      Boolean(searchParam && searchParam.trim()) &&
      matchesEverythingExceptSearch(ticket) &&
      !matchesSearchParam(ticket);

    socket.on("ready", () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });

        if (autoReorder && sortOrder === "unreadFirst") {
          dispatch({ type: "SORT_TICKETS", payload: { sortOrder } });
        }
      }

      if (data.action === "update") {
        if (!shouldUpdateTicket(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
          if (needsServerRecheck(data.ticket)) {
            setRefreshTrigger(Date.now());
          } else if (needsContactTagRecheck(data.ticket)) {
            resolveUnknownContactTags(data.ticket);
          }
          return;
        }

        // Salvar posição do scroll antes de reordenar
        saveScrollPosition();
        
        // Se autoReorder está habilitado, atualiza e reordena pela opção selecionada
        if (autoReorder) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket,
          });
          dispatch({ type: "SORT_TICKETS", payload: { sortOrder } });
        } else {
          // autoReorder desabilitado: atualiza in-place se existe, topo se é novo
          dispatch({
            type: "UPSERT_TICKET_TOP",
            payload: data.ticket,
          });
        }
        
        // Restaurar posição do scroll após atualização
        restoreScrollPosition();
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }

      // Conexão excluída: os tickets dela viraram inativos (fechados, só leitura).
      // Admin na lista de resolvidos recarrega para vê-los com o selo; nas demais
      // listas (e para quem não é admin) eles simplesmente saem.
      if (data.action === "connectionInactivated" && data.whatsappId) {
        if (isAdminUser(user) && (!status || status === "closed")) {
          setRefreshTrigger(Date.now());
        } else {
          dispatch({ type: "DELETE_TICKETS_BY_WHATSAPP", payload: data.whatsappId });
        }
      }
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      // O gate antigo aqui descartava QUALQUER ticket sem fila para o perfil
      // "user", ignorando allTicket e o escopo por conexão — era o que impedia o
      // ticket sem fila de uma conexão gerenciada de aparecer ao vivo.
      // `shouldUpdateTicket` já aplica a regra de acesso completa.
      if (data.action === "create" && needsContactTagRecheck(data.ticket)) {
        resolveUnknownContactTags(data.ticket);
        return;
      }

      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        // Salvar posição do scroll antes de reordenar
        saveScrollPosition();
        
        // Se autoReorder está habilitado, atualiza e reordena pela opção selecionada
        if (autoReorder) {
          dispatch({
            type: "UPDATE_TICKET_UNREAD_MESSAGES",
            payload: data.ticket,
          });
          dispatch({ type: "SORT_TICKETS", payload: { sortOrder } });
        } else {
          // autoReorder desabilitado: atualiza in-place se existe, topo se é novo
          dispatch({
            type: "UPSERT_TICKET_TOP",
            payload: data.ticket,
          });
        }
        
        // Restaurar posição do scroll após atualização
        restoreScrollPosition();
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });

        // Tags do contato alteradas (ContactTagsContainer/ContactDrawer): o
        // evento traz `contact.tags` recarregado.
        const contactId = Number(data.contact?.id);
        if (Number.isNaN(contactId) || !Array.isArray(data.contact?.tags)) return;

        const tagIds = toTagIds(data.contact.tags);
        contactTagsByContactRef.current.set(contactId, tagIds);

        if (!hasContactTagFilter) return;

        const matches = normalizedContactTagIds.some((id) => tagIds.includes(id));
        const inList = ticketsListRef.current.some(
          (t) => Number(t.contactId) === contactId
        );

        if (!matches && inList) {
          dispatch({ type: "DELETE_TICKETS_BY_CONTACT", payload: contactId });
        } else if (matches && !inList) {
          requestContactTagRecheck();
        }
      }
    });

    return () => {
      contactTagsAbortController.abort();
      // ✅ ManagedSocket.disconnect() remove APENAS os listeners e joins DESTE socket gerenciado
      // Não desconecta o socket global real — apenas faz cleanup dos callbacks registrados
      socket.disconnect();
    };
  }, [status, showAll, user, selectedQueueIds, selectedWhatsappIds, tags, contactTags, users, searchParam, searchBy, socketManager, autoReorder, sortOrder, saveScrollPosition, restoreScrollPosition, connectionScopeEnabled]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  useEffect(() => {
    if (typeof updateAlarmSummary === "function") {
      updateAlarmSummary(alarmSummary);
    }
  }, [alarmSummary, updateAlarmSummary]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Funções para gerenciar seleção
  const handleSelectionModeChange = useCallback((enabled, ticketStatus) => {
    setSelectionMode(enabled);
    if (enabled) {
      setSelectionStatus(ticketStatus);
    } else {
      setSelectionStatus(null);
      setSelectedTickets([]);
    }
  }, []);

  const handleSelectTicket = useCallback((ticket) => {
    // Ativar modo seleção automaticamente se não estiver ativo (para CTRL+click)
    if (!selectionMode) {
      setSelectionMode(true);
      setSelectionStatus(ticket.status);
      setSelectedTickets([ticket]);
      return;
    }
    
    // Só pode selecionar tickets do mesmo status
    if (selectionStatus && ticket.status !== selectionStatus) {
      return;
    }
    
    setSelectedTickets((prev) => {
      const isSelected = prev.some((t) => t.id === ticket.id);
      if (isSelected) {
        const newSelection = prev.filter((t) => t.id !== ticket.id);
        // Se não há mais seleção, desativar modo seleção
        if (newSelection.length === 0) {
          setSelectionMode(false);
          setSelectionStatus(null);
        }
        return newSelection;
      } else {
        return [...prev, ticket];
      }
    });
  }, [selectionMode, selectionStatus]);

  const handleSelectAll = useCallback(() => {
    const ticketsToSelect = ticketsList.filter(
      (t) => !selectionStatus || t.status === selectionStatus || t.status === status
    );
    setSelectedTickets(ticketsToSelect);
  }, [ticketsList, selectionStatus, status]);

  const handleClearSelection = useCallback(() => {
    setSelectedTickets([]);
    setSelectionMode(false);
    setSelectionStatus(null);
  }, []);

  // Funções para gerenciar menu de contexto (com posição do mouse)
  const handleContextMenu = useCallback((event, ticket) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Calcular posição limitada à janela visível
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    
    // Ajustar para não ultrapassar os limites da tela
    const menuWidth = 220; // largura aproximada do menu
    const menuHeight = 350; // altura aproximada do menu
    
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    const adjustedX = mouseX + menuWidth > windowWidth 
      ? windowWidth - menuWidth - 10 
      : mouseX;
    const adjustedY = mouseY + menuHeight > windowHeight 
      ? windowHeight - menuHeight - 10 
      : mouseY;
    
    const newPosition = {
      top: Math.max(10, adjustedY),
      left: Math.max(10, adjustedX),
    };
    
    // Se o menu já está aberto, guardar os novos dados e fechar primeiro
    if (contextMenuPosition) {
      pendingContextMenuRef.current = { position: newPosition, ticket };
      setContextMenuPosition(null);
      setContextMenuTicket(null);
    } else {
      // Menu fechado, abrir diretamente
      setContextMenuPosition(newPosition);
      setContextMenuTicket(ticket);
    }
  }, [contextMenuPosition]);

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(null);
    setContextMenuTicket(null);
    
    // Se há um menu pendente para abrir, abrir após fechar
    if (pendingContextMenuRef.current) {
      const { position, ticket } = pendingContextMenuRef.current;
      pendingContextMenuRef.current = null;
      // Usar requestAnimationFrame para garantir que o menu fechou
      requestAnimationFrame(() => {
        setContextMenuPosition(position);
        setContextMenuTicket(ticket);
      });
    }
  }, []);

  // Verifica se um ticket está selecionado
  const isTicketSelected = useCallback((ticketId) => {
    return selectedTickets.some((t) => t.id === ticketId);
  }, [selectedTickets]);

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      {/* Barra de seleção */}
      {selectionMode && selectedTickets.length > 0 && (
        <Box className={classes.selectionBar}>
          <Box className={classes.selectionInfo}>
            <Checkbox
              checked={selectedTickets.length === ticketsList.filter(t => !selectionStatus || t.status === selectionStatus).length}
              indeterminate={selectedTickets.length > 0 && selectedTickets.length < ticketsList.filter(t => !selectionStatus || t.status === selectionStatus).length}
              onChange={(e) => {
                if (e.target.checked) {
                  handleSelectAll();
                } else {
                  handleClearSelection();
                }
              }}
              style={{ color: "white" }}
            />
            <Typography variant="body2">
              {i18n.t("ticketContextMenu.selectedCount", { count: selectedTickets.length })}
            </Typography>
          </Box>
          <Box className={classes.selectionActions}>
            <Button
              size="small"
              variant="outlined"
              style={{ color: "white", borderColor: "white" }}
              onClick={handleClearSelection}
            >
              {i18n.t("ticketContextMenu.cancel")}
            </Button>
          </Box>
        </Box>
      )}
      
      <Paper
        ref={scrollContainerRef}
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              {connectionScopeEnabled &&
              !isAdminUser(user) &&
              Array.isArray(getManagedWhatsappIds(user)) &&
              getManagedWhatsappIds(user).length === 0 ? (
                <>
                  <span className={classes.noTicketsTitle}>
                    {i18n.t("ticketsList.noConnection.title")}
                  </span>
                  <p className={classes.noTicketsText}>
                    {i18n.t("ticketsList.noConnection.message")}
                  </p>
                </>
              ) : (
                <>
                  <span className={classes.noTicketsTitle}>
                    {i18n.t("ticketsList.noTicketsTitle")}
                  </span>
                  <p className={classes.noTicketsText}>
                    {i18n.t("ticketsList.noTicketsMessage")}
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem
                  ticket={ticket}
                  key={ticket.id}
                  selectionMode={selectionMode}
                  isSelected={isTicketSelected(ticket.id)}
                  onSelect={handleSelectTicket}
                  onContextMenu={handleContextMenu}
                  selectionStatus={selectionStatus}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>

      {/* Menu de contexto */}
      <TicketContextMenu
        anchorPosition={contextMenuPosition}
        open={Boolean(contextMenuPosition)}
        onClose={handleContextMenuClose}
        ticket={contextMenuTicket}
        selectedTickets={selectedTickets}
        onSelectionModeChange={handleSelectionModeChange}
        selectionMode={selectionMode}
        onSelectTicket={handleSelectTicket}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        tickets={ticketsList}
        currentStatus={status}
      />
    </Paper>
  );
};

export default TicketsListCustom;
